import {
  policyOverrideWrite,
  policyRequestCreate,
  policyRuleCreate,
  policyVersionDraft,
  policyVersionPublish,
  type ApiClient,
  type PolicyGuard,
  type PolicyGuardSetting,
  type PolicyScopeSettings,
  type RuleSpec,
} from '@aithora/shared';

/** Everything drafted on the Guards tab and not sent yet. */
export interface Drafts {
  /** Network guard switched (key: flip against live). */
  off: Record<string, true>;
  /** Network guard settings: 'key:FIELD' or 'key:act'. */
  edits: Record<string, string | number | boolean>;
  /** This building's override switched (key: flip against live). */
  fac: Record<string, true>;
  /** New custom rules. */
  rules: RuleSpec[];
  /** Scope settings. */
  scope: { blocked?: string[]; hold?: number | null; draftH?: number | null };
  /** A rollback draft the server already created, to publish on save. */
  rollback: { id: string; label: string } | null;
}

/**
 * Review & save. A Facility Admin sends one change request for their own building; the
 * owner decides. Aithora staff write a network draft and publish it (the server versions
 * it and seals it), then add any new rules. Every write carries confirm: true, since the
 * person has just confirmed the "Before you save" panel.
 */
export async function savePolicy(
  client: ApiClient,
  o: { isFA: boolean; canNet: boolean; scope: string | null; drafts: Drafts; guards: PolicyGuard[]; scopeSaved: PolicyScopeSettings | null },
): Promise<string> {
  const d = o.drafts;
  if (o.isFA) {
    const overrides: Record<string, boolean> = {};
    for (const k of Object.keys(d.fac)) {
      const g = o.guards.find((x) => x.key === k);
      if (g) overrides[k] = !g.enabled;
    }
    await policyRequestCreate(client, { body: { overrides, customRules: d.rules, confirm: true } });
    return 'Sent to Aithora for approval';
  }
  if (d.rollback) await policyVersionPublish(client, { params: { id: d.rollback.id }, body: { confirm: true } });
  const guardKeys = new Set([...Object.keys(d.off), ...Object.keys(d.edits).map((k) => k.split(':')[0])]);
  const scopeChanged = d.scope.blocked !== undefined || d.scope.hold !== undefined || d.scope.draftH !== undefined;
  if (o.canNet && (guardKeys.size || scopeChanged)) {
    const guards: Record<string, Partial<PolicyGuardSetting>> = {};
    for (const k of guardKeys) {
      const g = o.guards.find((x) => x.key === k);
      if (!g) continue;
      const values = { ...g.network.values };
      let action = g.network.action;
      for (const [ek, v] of Object.entries(d.edits)) {
        const [gk, field] = ek.split(':');
        if (gk !== k) continue;
        if (field === 'act') action = String(v);
        else values[field] = v;
      }
      guards[k] = { enabled: d.off[k] ? !g.network.enabled : g.network.enabled, values, action };
    }
    const scope = scopeChanged
      ? {
          blockedTopics: d.scope.blocked ?? o.scopeSaved?.blockedTopics ?? [],
          escalationHoldMinutes: d.scope.hold !== undefined ? d.scope.hold : o.scopeSaved?.escalationHoldMinutes ?? null,
          draftTtlHours: d.scope.draftH !== undefined ? d.scope.draftH : o.scopeSaved?.draftTtlHours ?? null,
        }
      : undefined;
    const draft = await policyVersionDraft(client, { body: { guards: Object.keys(guards).length ? guards : undefined, scope, confirm: true } });
    await policyVersionPublish(client, { params: { id: draft.version.id }, body: { confirm: true } });
  }
  let waiting = 0;
  // A building's override set by Aithora staff: the owner's applies at once, anyone
  // else's waits for the owner (PUT /policy/overrides).
  const facKeys = Object.keys(d.fac);
  if (o.scope && o.scope !== 'network' && facKeys.length) {
    const overrides: Record<string, boolean> = {};
    for (const k of facKeys) {
      const g = o.guards.find((x) => x.key === k);
      if (g) overrides[k] = !g.enabled;
    }
    const out = await policyOverrideWrite(client, { body: { scope: o.scope, overrides, confirm: true } });
    if (!out.applied) waiting++;
  }
  for (const r of d.rules) {
    const out = await policyRuleCreate(client, { body: { ...r, scope: o.scope ?? undefined, confirm: true } });
    if (out.changeRequestId) waiting++;
  }
  return waiting ? `Saved. ${waiting} change${waiting === 1 ? '' : 's'} waiting on the owner` : 'Saved';
}
